import React, { useState, useMemo, useEffect, useContext, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import Login from "./Login";

import { getRefreshToken } from "./utils";
import AuthContext from "./AuthContext";

import "./App.css";
import "./components/design-system/styles.css";
// import Register from "./Register";
// import Pending from "./Pending";
import Main from "./Main";

// import Password from "./Password";
// import Forgot from "./Forgot";
import backend, { authContextRef, authBackend } from "./backend";
// import Created from "./Created";

import "./custom.scss";
import "bootstrap-icons/font/bootstrap-icons.css";

import Forgot from "./Forgot";
import Pending from "./Pending";
import Password from "./Password";
import Pieces from "./screens/pieces/Pieces";
import Inventory from "./screens/Inventory";
import Purchases from "./screens/Purchases";
import Entities from "./screens/entities/Entities";
import Entity from "./screens/entities/Entity";
import EntityForm from "./screens/entities/EntityForm";
import Piece from "./screens/pieces/Piece";
import PieceForm from "./screens/pieces/PieceForm";
import Machine from "./screens/machines/Machine";
import Machines from "./screens/machines/Machines";
import MachineForm from "./screens/machines/MachineForm";
import MachineTypeForm from "./screens/machines/MachineTypeForm";
import QuotationForm from "./screens/orders/QuotationForm";
import Settings from "./screens/settings/Settings";
import GeneralPanel from "./screens/settings/panels/GeneralPanel";
import PricesPanel from "./screens/settings/panels/PricesPanel";
import DrivePanel from "./screens/settings/panels/DrivePanel";
import UsersPanel from "./screens/settings/panels/UsersPanel";
import MyAccountPanel from "./screens/settings/panels/MyAccountPanel";
import MustResetPasswordModal from "./screens/settings/panels/MustResetPasswordModal";
import Chart from "./screens/chart/Chart";
import Dashboard from "./screens/dashboard/Dashboard";
import CustomerOrders from "./screens/customerOrders/CustomerOrders";
import CustomerOrder from "./screens/customerOrders/CustomerOrder";
import CustomerOrderForm from "./screens/customerOrders/CustomerOrderForm";
import WorkOrders from "./screens/workOrders/WorkOrders";
import WorkOrderForm from "./screens/workOrders/WorkOrderForm";
import Produccion from "./screens/produccion/ProduccionV2";
import Quotations from "./screens/quotations/Quotations";
import Precios from "./screens/precios/Precios";
import Contabilidad from "./screens/contabilidad/Contabilidad";
import Facturacion from "./screens/facturacion/Facturacion";
import Rendiciones from "./screens/rendiciones/Rendiciones";
import Trabajadores from "./screens/rrhh/Trabajadores";
import Liquidaciones from "./screens/rrhh/Liquidaciones";
import Asesores from "./screens/asesores/Asesores";
import { HeimdallPagina } from "./components/HeimdallPanel";
import Pulido from "./screens/pulido/Pulido";
import Documentos from "./screens/documentos/Documentos";
import Auditoria from "./screens/auditoria/Auditoria";
import Cuadrilla from "./screens/cuadrilla/Cuadrilla";
import Tablero from "./screens/tablero/Tablero";
import Biblioteca from "./screens/biblioteca/Biblioteca";
import Emitir from "./screens/emitir/Emitir";
import OcEntrantes from "./screens/mail/OcEntrantes";

// Aterrizaje por rol (28-ago): el seller entra directo a SU pantalla de trabajo
// (/quotations); el resto mantiene el aterrizaje histórico en /dashboard.
//
// 29-ago (SEGURIDAD #6): cuál es esa pantalla lo dice el servidor en
// `descriptor.home` (backend/config/roles.js). Acá había una tercera copia de la
// tabla de roles, distinta de las otras dos.
//
// Mientras el descriptor viaja no se navega a ninguna parte: mandar a /dashboard
// "por mientras" y corregir después le haría parpadear la pantalla equivocada al
// vendedor en cada entrada.
const RoleLanding = () => {
  const authContext = useContext(AuthContext);
  const home = authContext?.descriptor?.home;
  if (!home) return <div style={{ padding: 24, color: "#888", fontSize: 13 }}>Cargando…</div>;
  return <Navigate to={home} replace />;
};

// ── Modo "Ver como" (admin) ──────────────────────────────────────────────────
// Lente VISUAL: el admin cambia los roles EFECTIVOS que ve el frontend sin
// tocar el JWT ni el backend (que sigue viendo admin). Se persiste en
// sessionStorage a propósito: se pierde al cerrar el navegador.
const VISTA_COMO_KEY = "bini_vista_como";
const VISTAS_VALIDAS = ["operator", "seller", "accountant"];

const App = () => {
  const [logged, setLogged] = useState(false);

  // Vista activa del modo "Ver como" (null = vista real). Se rehidrata de
  // sessionStorage al recargar; el getter de `user` igual exige admin real,
  // así un valor viejo en manos de un no-admin no tiene efecto.
  const [vistaComo, setVistaComoState] = useState(() => {
    try {
      const v = sessionStorage.getItem(VISTA_COMO_KEY);
      return VISTAS_VALIDAS.includes(v) ? v : null;
    } catch {
      return null;
    }
  });

  // Usuario REAL de la sesión. Login.js lo setea por mutación directa
  // (`authContext.user = ...`); el setter de abajo lo guarda acá.
  const userRealRef = useRef(null);

  // Descriptor de rol servido por la API (SEGURIDAD #5/#6): menú, barra inferior,
  // aterrizaje y capacidades. El front ya no tiene su propia copia de esa tabla.
  const [descriptor, setDescriptor] = useState(null);

  // Force logout for users on the legacy persistent-localStorage flow.
  useEffect(() => {
    try {
      if (localStorage.getItem("dx") || localStorage.getItem("pk")) {
        localStorage.removeItem("dx");
        localStorage.removeItem("pk");
      }
    } catch {}
  }, []);

  // Se pide al entrar y cada vez que cambia "Ver como": el header x-vista-como lo
  // agrega solo el interceptor de backend.js, así que el descriptor que vuelve es
  // el del rol que el admin está mirando — el menú y los permisos por fin dicen lo
  // mismo.
  //
  // Si falla, se reintenta UNA vez y después se deja el descriptor anterior en
  // pie: perder el menú por un hipo de red sería peor que mostrarlo un poco
  // viejo. Al salir se limpia.
  useEffect(() => {
    if (!logged) {
      setDescriptor(null);
      return undefined;
    }
    let vivo = true;
    let reintento;
    const pedir = async (esReintento = false) => {
      try {
        const { data } = await backend.get("user/me/descriptor");
        if (vivo) setDescriptor(data);
      } catch (err) {
        if (!vivo) return;
        if (!esReintento) {
          reintento = setTimeout(() => pedir(true), 1500);
          return;
        }
        console.log("no se pudo traer el descriptor de rol:", err?.message);
      }
    };
    pedir();
    return () => {
      vivo = false;
      clearTimeout(reintento);
    };
  }, [logged, vistaComo]);

  const authContext = useMemo(() => {
    // Activa/desactiva "Ver como". SOLO un admin real puede activarla;
    // volver a null siempre está permitido.
    const setVistaComo = rol => {
      if (rol !== null && !VISTAS_VALIDAS.includes(rol)) return;
      const rolesReales = userRealRef.current?.roles || [];
      if (rol !== null && !rolesReales.includes("admin")) return;
      try {
        if (rol) sessionStorage.setItem(VISTA_COMO_KEY, rol);
        else sessionStorage.removeItem(VISTA_COMO_KEY);
      } catch {}
      setVistaComoState(rol);
    };

    const ctx = {
      login: () => {
        setLogged(true);
      },
      logout: async () => {
        // console.log("logout pressed");

        try {
          const refreshToken = getRefreshToken();

          await authBackend.post("/user/logout", { refreshToken });
          // console.log("logout");
        } catch (err) {
          // console.log("authContext logout", err);
          console.log(err);
        }

        try {
          sessionStorage.removeItem("dx");
          sessionStorage.removeItem("pk");
          sessionStorage.removeItem(VISTA_COMO_KEY);
          // Cleanup legacy persistent tokens (pre sessionStorage migration)
          localStorage.removeItem("dx");
          localStorage.removeItem("pk");
        } catch {}
        userRealRef.current = null;
        setVistaComoState(null);
        setLogged(false);
      },
      signUp: async data => {
        console.log("meh");
      },
      vistaComo,
      setVistaComo,
      descriptor,
      // `can` para el paquete #7 del plan: las pantallas preguntan por la
      // capacidad y no por el rol. Sin descriptor todavía, nadie puede nada —
      // que es la respuesta segura mientras no se sabe.
      can: capacidad => (descriptor?.capacidades || []).includes(capacidad),
    };

    // `user` se llena por mutación desde Login.js (authContext.user = ...):
    // el setter guarda el usuario REAL y el getter entrega el usuario con
    // roles EFECTIVOS — si vistaComo está activa y el real es admin, los
    // roles pasan a ser [vistaComo]. Así todos los consumidores de
    // auth.user.roles (SideBar, RoleLanding, PulsoTaller, Quotations, etc.)
    // reaccionan sin tocarlos uno a uno. El JWT y el backend no cambian.
    let cacheReal = null;
    let cacheEfectivo = null;
    Object.defineProperty(ctx, "user", {
      enumerable: true,
      configurable: true,
      get() {
        const real = userRealRef.current;
        if (!real) return real;
        if (vistaComo && (real.roles || []).includes("admin")) {
          // Identidad estable mientras el usuario real no cambie (evita
          // re-disparar effects que dependan de auth.user).
          if (cacheReal !== real) {
            cacheReal = real;
            cacheEfectivo = { ...real, roles: [vistaComo] };
          }
          return cacheEfectivo;
        }
        return real;
      },
      set(value) {
        userRealRef.current = value;
        cacheReal = null;
        cacheEfectivo = null;
      },
    });

    // Roles reales de la sesión, sin el lente de vistaComo (para decidir
    // quién ve el selector "Ver como" y el banner).
    Object.defineProperty(ctx, "rolesReales", {
      enumerable: true,
      configurable: true,
      get() {
        return userRealRef.current?.roles || [];
      },
    });

    return ctx;
  }, [vistaComo, descriptor]);

  useEffect(() => {
    authContextRef.current = authContext;
  }, [authContext]);

  return (
    <>
      <AuthContext.Provider value={authContext}>
        <BrowserRouter>
          <Routes>
            <Route path="/">
              <Route path="pending" element={<Pending />} />
              {/* <Route exact path="/created" component={Created} /> */}
              <Route path="forgot" element={<Forgot />} />
              <Route path="password/:token" element={<Password />} />
              {logged ? (
                <>
                  <Route
                    path="error"
                    element={<div style={{ color: "black" }}>error</div>}
                  />
                  <Route path="/" element={<Main />}>
                    <Route path="home" element={<Chart />} />
                    <Route path="dashboard" element={<Dashboard />} />
                    {/* Legacy: /orders ("Pedidos") retirado — el flujo vive en
                        /workOrders y la ruta antigua redirige. */}
                    <Route path="orders" element={<Navigate to="/workOrders" replace />} />
                    <Route path="orders/*" element={<Navigate to="/workOrders" replace />} />

                    <Route path="machines" element={<Machines />}>
                      <Route path="view" element={<Machine />} />
                      <Route path="new" element={<MachineForm />} />
                      <Route path="new_type" element={<MachineTypeForm />} />
                      <Route path="edit" element={<MachineForm />} />
                      <Route path="edit_type" element={<MachineTypeForm />} />
                    </Route>
                    <Route path="pieces" element={<Pieces />}>
                      <Route path="view" element={<Piece />} />
                      <Route path="edit" element={<PieceForm />} />
                    </Route>
                    <Route path="pieces/new" element={<PieceForm />} />

                    <Route path="inventory" element={<Inventory />} />
                    <Route path="purchases" element={<Purchases />} />
                    <Route path="entities" element={<Entities />}>
                      <Route path="view" element={<Entity />} />
                      <Route path="new" element={<EntityForm />} />
                      <Route path="edit" element={<EntityForm />} />
                    </Route>
                    <Route path="customerOrders" element={<CustomerOrders />}>
                      <Route path="view" element={<CustomerOrder />} />
                    </Route>
                    <Route path="customerOrders/new" element={<CustomerOrderForm />} />
                    <Route path="workOrders" element={<WorkOrders />} />
                    <Route path="workOrders/new" element={<WorkOrderForm />} />
                    <Route path="produccion" element={<Produccion />} />
                    <Route path="quotations" element={<Quotations />} />
                    <Route path="quotations/new" element={<QuotationForm />} />
                    <Route path="precios" element={<Precios />} />
                    <Route path="contabilidad" element={<Contabilidad />} />
                    <Route path="facturacion" element={<Facturacion />} />
                    <Route path="rendiciones" element={<Rendiciones />} />
                    <Route path="rrhh" element={<Trabajadores />} />
                    <Route path="liquidaciones" element={<Liquidaciones />} />
                    <Route path="asesores" element={<Asesores />} />
                    <Route path="heimdall" element={<HeimdallPagina />} />
                    <Route path="documentos" element={<Documentos />} />
                    <Route path="auditoria" element={<Auditoria />} />
                    <Route path="cuadrilla" element={<Cuadrilla />} />
                    <Route path="tablero" element={<Tablero />} />
                    {/* Biblioteca: el conocimiento de la cuadrilla para leer con calma (solo admin). */}
                    <Route path="biblioteca" element={<Biblioteca />} />
                    {/* Emisión desde el teléfono: la pantalla se autoprotege por rol. */}
                    <Route path="emitir" element={<Emitir />} />
                    {/* Sin ítem de menú: se llega por la tarjeta "OC por registrar" del Dashboard */}
                    <Route path="oc-entrantes" element={<OcEntrantes />} />
                    <Route path="pulido" element={<Pulido />} />
                    <Route path="settings" element={<Settings />}>
                      <Route index element={<Navigate to="general" replace />} />
                      <Route path="general" element={<GeneralPanel />} />
                      <Route path="prices" element={<PricesPanel />} />
                      <Route path="drive" element={<DrivePanel />} />
                      <Route path="users" element={<UsersPanel />} />
                      <Route path="me" element={<MyAccountPanel />} />
                    </Route>
                    <Route path="/" element={<RoleLanding />} />
                  </Route>
                </>
              ) : (
                <>
                  <Route path="/" element={<Login />} />
                  <Route path="*" element={<Login />} />
                </>
              )}
            </Route>
          </Routes>
          {logged && <MustResetPasswordModal />}
        </BrowserRouter>
      </AuthContext.Provider>
    </>
  );
};

export default App;
